import React, { useEffect, useMemo, useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import {
  DateInput,
  Header,
  Loadable,
  useApiGet,
  varClass,
} from '../shared'
import {
  IAnomaliesV2Alert,
  IAnomaliesV2DetailResponse,
  IAnomaliesV2DetailSelected,
  IAnomaliesV2Network,
  IAnomaliesV2RelatedAlert,
  IAnomaliesV2SeriesPoint,
  IAnomaliesV2SeriesPointNetworkRef,
  TAnomaliesAlertFamily,
  TAnomaliesDevice,
  TAnomaliesPrimaryMetric,
} from '../shared/types'
import {
  AlertPeriod,
  FAMILY_COLORS,
  FAMILY_LABELS,
  METRIC_LABELS,
  METRIC_SHORT,
  badgesFor,
  displayLabelFor,
  formatDecimal,
  formatDkkLarge,
  formatDkkPrecise,
  formatInteger,
  formatPercent,
  formatScore,
  formatVsAnchor,
  groupAlertPeriods,
  isCommissionMissing,
} from './shared'
import { CommercialChart } from './charts/CommercialChart'
import { DriftRatioChart, ZScoreChart } from './charts/EvidenceChart'
import { NetworkLane } from './charts/NetworkLane'
import { PrimaryMetricChart } from './charts/PrimaryMetricChart'
import { VolumeChart } from './charts/VolumeChart'

const DEVICES: TAnomaliesDevice[] = ['Mobile', 'Desktop']
const METRICS: TAnomaliesPrimaryMetric[] = [
  'cr',
  'epc',
  'spc',
  'clicks',
  'conversions',
]
type RangeKey = '90d' | '180d' | '400d' | 'all'

const dateOnly = (value?: string | null): string | null => {
  if (!value) return null
  return value.slice(0, 10)
}

const safeFormat = (value: string | null | undefined, pattern: string): string | null => {
  if (!value) return null
  try {
    return format(parseISO(value), pattern)
  } catch {
    return null
  }
}

interface UrlState {
  date: string | null
  device: TAnomaliesDevice | null
  metric: TAnomaliesPrimaryMetric | null
  days: number | null
}

const parseUrl = (search?: string): UrlState => {
  const out: UrlState = { date: null, device: null, metric: null, days: null }
  if (!search) return out
  const p = new URLSearchParams(search)
  const date = p.get('date')
  if (date) out.date = date
  const device = p.get('device')
  if (device === 'Mobile' || device === 'Desktop') out.device = device
  const metric = p.get('metric') as TAnomaliesPrimaryMetric | null
  if (metric && METRICS.includes(metric)) out.metric = metric
  const days = p.get('days')
  if (days) {
    const n = parseInt(days, 10)
    if (!isNaN(n)) out.days = n
  }
  return out
}

const buildSearch = (state: UrlState): string => {
  const params = new URLSearchParams()
  if (state.date) params.set('date', state.date)
  if (state.device) params.set('device', state.device)
  if (state.metric) params.set('metric', state.metric)
  if (state.days) params.set('days', String(state.days))
  const s = params.toString()
  return s ? `?${s}` : ''
}

export const DetailView = (props: {
  advertiserDomain: string
  search?: string
}) => {
  const advertiserDomain = decodeURIComponent(props.advertiserDomain)
  const url = parseUrl(props.search)

  // Local state mirrors URL but lets in-page toggles work without hard
  // navigation. Pushed back to the URL via replaceState on change.
  const [date, setDate] = useState<string | null>(url.date)
  const [device, setDevice] = useState<TAnomaliesDevice | null>(url.device)
  const [metric, setMetric] = useState<TAnomaliesPrimaryMetric | null>(url.metric)
  const [days, setDays] = useState<number>(url.days || 180)
  // Tracks whether the user has explicitly chosen a range. If they haven't,
  // we'll auto-bump to 400d once the detector tells us the alert family is
  // drift (drift wants more history; acute/rejection are fine at 180d).
  const userTouchedDays = useRef<boolean>(url.days != null)

  // Push state changes to URL so links are shareable. replaceState avoids
  // bloating browser history with every toggle.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const search = buildSearch({ date, device, metric, days })
    const target = `/anomalies/${encodeURIComponent(advertiserDomain)}${search}`
    if (
      window.location.pathname + window.location.search !== target
    ) {
      window.history.replaceState({}, '', target)
    }
  }, [advertiserDomain, date, device, metric, days])

  const detailParams = useMemo(() => {
    const params: Record<string, string | number> = {
      advertiser_domain: advertiserDomain,
      days,
    }
    if (date) params.date = date
    if (device) params.device = device
    if (metric) params.metric = metric
    return params
  }, [advertiserDomain, date, device, metric, days])

  const detail = useApiGet<IAnomaliesV2DetailResponse>(
    '/anomalies/v2/detail',
    detailParams,
    [advertiserDomain, date, device, metric, days]
  )

  // Backend returns `selected` once it's resolved every default. Until then
  // fall back to whatever local state we have.
  const selected: IAnomaliesV2DetailSelected | null = detail?.selected || null
  const resolvedDate = dateOnly(selected?.date) || date || dateOnly(detail?.selectedDate) || null
  const resolvedDevice = selected?.device || device || detail?.device || null
  const resolvedMetric: TAnomaliesPrimaryMetric =
    selected?.metric || metric || detail?.selectedAlert?.primaryMetric || 'cr'
  const resolvedDays = selected?.days || days

  const sameDateAlerts = detail?.sameDateAlerts || []
  const allSeries = detail?.series || []
  // Filter to the resolved device when the response carries multiple devices.
  const series = useMemo(() => {
    if (!resolvedDevice) return allSeries
    const hasDeviceField = allSeries.some(
      (p) => (p as any).device != null
    )
    if (!hasDeviceField) return allSeries
    return allSeries.filter((p) => (p as any).device === resolvedDevice)
  }, [allSeries, resolvedDevice])

  const selectedPoint = useMemo(() => {
    if (!resolvedDate) return undefined
    return series.find((p) => p.eventDate === resolvedDate)
  }, [series, resolvedDate])

  // Synthesize an alert object when selectedAlert is missing — drift signals
  // that aren't firing on the selected day still want their family/metric
  // labels in the header.
  const selectedAlert: IAnomaliesV2Alert | null =
    detail?.selectedAlert ||
    buildAlertFromSeries(
      selectedPoint,
      advertiserDomain,
      resolvedDevice || 'Mobile',
      resolvedDate || ''
    )

  const alertHistory = detail?.alertHistory || []

  // True when the selected (device, metric, date) has an alert in any of the
  // known lists. The check is loose on device when device wasn't pinned by
  // the URL (advertiser-only mode) — any device match on the selected day
  // counts as "investigating".
  const hasAlertForSelection = useMemo(() => {
    if (!resolvedDate) return false

    const matches = (a: {
      eventDate?: string
      device?: string
      primaryMetric?: string
    }): boolean => {
      if (a.eventDate?.slice(0, 10) !== resolvedDate) return false
      if (resolvedDevice && a.device !== resolvedDevice) return false
      if (a.primaryMetric !== resolvedMetric) return false
      return true
    }

    if (detail?.selectedAlert && matches(detail.selectedAlert)) return true
    if (sameDateAlerts.some(matches)) return true
    if (alertHistory.some(matches)) return true
    return false
  }, [detail, sameDateAlerts, alertHistory, resolvedDate, resolvedDevice, resolvedMetric])
  const latestAlertInHistory = alertHistory.length
    ? alertHistory.reduce((acc, a) =>
        a.eventDate > acc.eventDate ? a : acc
      )
    : null

  // Auto-pick a sensible default range based on detected alert family. Only
  // fires once and only if the user hasn't manually chosen a range yet.
  useEffect(() => {
    if (userTouchedDays.current) return
    const family = detail?.selectedAlert?.alertFamily
    if (!family) return
    const wanted = family === 'drift' ? 400 : 180
    if (days !== wanted) setDays(wanted)
  }, [detail?.selectedAlert?.alertFamily])

  const onPickDate = (newDate: Date | null) => {
    setDate(newDate ? format(newDate, 'yyyy-MM-dd') : null)
  }

  // Period-row click hand-off: clicking a related-alert period sets the
  // selected date / device / metric in-page (and the URL effect propagates).
  const onPickFromAlert = (row: IAnomaliesV2RelatedAlert) => {
    setDate(row.eventDate)
    setDevice(row.device)
    setMetric(row.primaryMetric)
    userTouchedDays.current = true
    setDays(row.alertFamily === 'drift' ? 400 : 180)
  }

  // Health-matrix cell click: keep the same date but switch (device, metric).
  // Don't auto-bump days — leave the user's range intact.
  const onPickCell = (
    cellDevice: TAnomaliesDevice,
    cellMetric: TAnomaliesPrimaryMetric
  ) => {
    setDevice(cellDevice)
    setMetric(cellMetric)
  }

  const onPickDevice = (d: TAnomaliesDevice) => {
    setDevice(d)
  }
  const onPickMetric = (m: TAnomaliesPrimaryMetric) => {
    setMetric(m)
  }
  // "All" means the full window the backend has data for — derive it from
  // available.minDate/maxDate so we don't ask for years of empty days that
  // hit the backend's cap and return nothing.
  const onPickRange = (key: RangeKey) => {
    userTouchedDays.current = true
    if (key === 'all') {
      const min = detail?.available?.minDate
      const max = detail?.available?.maxDate || resolvedDate
      if (min && max) {
        const span = Math.max(
          90,
          Math.ceil(
            (parseISO(max).getTime() - parseISO(min).getTime()) / 86400000
          )
        )
        // Clamp to a sane upper bound so we don't accidentally send years
        // when the backend caps responses at e.g. 730d.
        setDays(Math.min(span, 730))
      } else {
        // Defensive fallback — still smaller than the "fits-everything" 1825
        // we used to send.
        setDays(730)
      }
    } else {
      setDays(parseInt(key, 10))
    }
  }

  const available = detail?.available
  const availableDevices = available?.devices || DEVICES
  const availableMetrics = available?.metrics || METRICS

  return (
    <div className="anomalies-page anomalies-detail">
      <Header backPath="/anomalies" backLabel="Back to triage" />

      <Loadable data={detail}>
        <div className="row row-center row-space-between m-t-2">
          <div className="anomalies-detail-headline text-dark text-bigger text-bolder">
            {advertiserDomain}
          </div>
          <a
            href={`/anomalies/rejection-review/${encodeURIComponent(advertiserDomain)}`}
            className="anomalies-faq-toggle"
            title="Open the rejection review for this advertiser"
          >
            Rejection review →
          </a>
        </div>

        <ControlBar
          date={resolvedDate}
          onDateChange={onPickDate}
          device={resolvedDevice}
          devices={availableDevices}
          onDeviceChange={onPickDevice}
          metric={resolvedMetric}
          metrics={availableMetrics}
          onMetricChange={onPickMetric}
          days={resolvedDays}
          onRangeChange={onPickRange}
        />

        <div className="anomalies-detail-top-grid m-t-3">
          <StatusBanner
            mode={hasAlertForSelection ? 'investigating' : 'noAlert'}
            alert={hasAlertForSelection ? selectedAlert : null}
            selectedDate={resolvedDate || undefined}
            selectedDevice={resolvedDevice || undefined}
            selectedMetric={resolvedMetric}
            latestAlertDate={latestAlertInHistory?.eventDate || null}
            onPickLatestAlert={
              latestAlertInHistory
                ? () => onPickFromAlert(latestAlertInHistory)
                : undefined
            }
            alertCount={alertHistory.length}
            networks={detail?.networks || []}
            todayNetworks={selectedPoint?.networks || []}
          />
          <HealthMatrix
            sameDateAlerts={sameDateAlerts}
            alertHistory={alertHistory}
            selectedAlert={selectedAlert}
            selectedDate={resolvedDate}
            selectedDevice={resolvedDevice}
            selectedMetric={resolvedMetric}
            onPick={onPickCell}
          />
        </div>

        <MetricEvidenceCards
          metric={resolvedMetric}
          point={selectedPoint}
          alert={selectedAlert}
          device={resolvedDevice}
          selectedDate={resolvedDate}
        />

        <div className="anomalies-detail-charts-primary m-t-4">
          <PrimaryMetricChart
            series={series}
            family={selectedAlert?.alertFamily || 'drift'}
            metric={resolvedMetric}
          />
          <NetworkLane
            series={series}
            networks={detail?.networks || []}
            selectedDate={resolvedDate || undefined}
          />
        </div>
        <div className="anomalies-detail-charts-secondary m-t-3">
          <VolumeChart series={series} />
          <CommercialChart series={series} />
        </div>

        <div className="anomalies-related-history-grid m-t-4">
          <RelatedAlertsPanel
            advertiserDomain={advertiserDomain}
            sameDate={sameDateAlerts}
            nearby={detail?.nearbyAlerts || []}
            onPick={onPickFromAlert}
          />
          <AlertHistorySection
            advertiserDomain={advertiserDomain}
            alerts={alertHistory}
            onPick={onPickFromAlert}
          />
        </div>

        <details className="anomalies-advanced m-t-4">
          <summary className="text-dark text-bolder">
            Show advanced detector evidence
          </summary>
          <div className="anomalies-advanced-sections m-t-3">
            <div className="card">
              <div className="card-body">
                <div className="text-dark text-bolder m-b-2">
                  Detector summary — all metrics
                </div>
                <AdvancedDetectorTable
                  point={selectedPoint}
                  series={series}
                  selectedDate={resolvedDate || undefined}
                />
              </div>
            </div>

            <DriftRatioChart series={series} metric={resolvedMetric} />
            <ZScoreChart series={series} metric={resolvedMetric} />

          </div>
        </details>
      </Loadable>
    </div>
  )
}

const buildAlertFromSeries = (
  point: IAnomaliesV2SeriesPoint | undefined,
  advertiserDomain: string,
  device: TAnomaliesDevice,
  selectedDate: string
): IAnomaliesV2Alert | null => {
  if (!point) return null
  const family = (point as any).alertFamily as string | undefined
  const m = (point as any).primaryMetric as string | undefined
  if (!family || !m || family === 'UNCLASSIFIED_BUG') return null
  const reason = ((point as any).primaryReason as string) || ''
  const score = ((point as any).priorityScore as number) ?? 0
  return {
    rank: 0,
    eventDate: selectedDate,
    advertiserDomain,
    device,
    alertFamily: family as TAnomaliesAlertFamily,
    primaryMetric: m as TAnomaliesPrimaryMetric,
    displayReason: reason,
    reasonText: reason,
    currentValue: null,
    baselineValue: null,
    percentChange: null,
    estimatedLossLabel: null,
    priorityScore: score,
    isFirstAlertDay: false,
    isWorseningToday: false,
    alertFirstSeenDate: '',
    alertDaysActive: 0,
  }
}


// ---------------------------------------------------------------------------
// Top control bar — date / device / metric / range. Always available.
// ---------------------------------------------------------------------------
const ControlBar = (props: {
  date: string | null
  onDateChange: (date: Date | null) => void
  device: TAnomaliesDevice | null
  devices: TAnomaliesDevice[]
  onDeviceChange: (d: TAnomaliesDevice) => void
  metric: TAnomaliesPrimaryMetric
  metrics: TAnomaliesPrimaryMetric[]
  onMetricChange: (m: TAnomaliesPrimaryMetric) => void
  days: number
  onRangeChange: (key: RangeKey) => void
}) => {
  const dateValue = props.date ? parseISO(props.date) : null

  const isRangeActive = (key: RangeKey): boolean => {
    // Anything beyond the explicit 90/180/400 buckets counts as "All".
    if (key === 'all') return ![90, 180, 400].includes(props.days)
    return parseInt(key, 10) === props.days
  }

  return (
    <div className="anomalies-detail-controls m-t-3">
      <div className="row row-center" style={{ gap: 16, flexWrap: 'wrap' }}>
        <div className="control control-date" style={{ width: 160 }}>
          <DateInput
            selected={dateValue}
            onChange={props.onDateChange}
            blankValue="No date"
            isClearable
          />
        </div>
        <ToggleGroup label="Device">
          {props.devices.map((d) => (
            <button
              key={d}
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': props.device === d,
              })}
              onClick={() => props.onDeviceChange(d)}
            >
              {d}
            </button>
          ))}
        </ToggleGroup>
        <ToggleGroup label="Metric">
          {props.metrics.map((m) => (
            <button
              key={m}
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': props.metric === m,
              })}
              onClick={() => props.onMetricChange(m)}
            >
              {METRIC_SHORT[m]}
            </button>
          ))}
        </ToggleGroup>
        <ToggleGroup label="Range">
          {(['90d', '180d', '400d', 'all'] as RangeKey[]).map((r) => (
            <button
              key={r}
              type="button"
              className={varClass({
                'anomalies-trend-toggle': true,
                'active': isRangeActive(r),
              })}
              onClick={() => props.onRangeChange(r)}
            >
              {r}
            </button>
          ))}
        </ToggleGroup>
      </div>
    </div>
  )
}

const ToggleGroup = (props: {
  label: string
  children: React.ReactNode
}) => (
  <div className="row row-center" style={{ gap: 8 }}>
    <span className="text-light text-small">{props.label}</span>
    <div className="anomalies-trend-toggle-group">{props.children}</div>
  </div>
)

// ---------------------------------------------------------------------------
// Status banner — switches between "Investigating alert" and "No active
// alert for this selection" so the user always knows what mode the page is
// in.
// ---------------------------------------------------------------------------
const StatusBanner = (props: {
  mode: 'investigating' | 'noAlert'
  alert: IAnomaliesV2Alert | null
  selectedDate?: string
  selectedDevice?: TAnomaliesDevice
  selectedMetric: TAnomaliesPrimaryMetric
  latestAlertDate: string | null
  onPickLatestAlert?: () => void
  alertCount: number
  networks: IAnomaliesV2Network[]
  todayNetworks: IAnomaliesV2SeriesPointNetworkRef[]
}) => {
  const {
    mode,
    alert,
    selectedDate,
    selectedDevice,
    selectedMetric,
    latestAlertDate,
    onPickLatestAlert,
    alertCount,
    networks,
    todayNetworks,
  } = props
  const dateText = selectedDate ? safeFormat(selectedDate, 'MMM d, yyyy') : null

  if (mode === 'investigating' && alert) {
    const familyColor = FAMILY_COLORS[alert.alertFamily]
    const badges = badgesFor(alert)
    // Subtitle like "EPC drift on Mobile" — pulls metric short, family label,
    // and device. Sits below the advertiser headline.
    const titleParts: string[] = []
    titleParts.push(METRIC_SHORT[alert.primaryMetric])
    titleParts.push(FAMILY_LABELS[alert.alertFamily].toLowerCase())
    const subtitle = `${titleParts.join(' ')} on ${alert.device}`

    // Explanation: prefer reasonText (free-form), fall back to displayLabel.
    // Cleanups:
    //  - replace legacy "anchor" wording with "baseline"
    //  - replace "previous 6m" with "6m baseline" so the copy is consistent
    //    with the rest of the UI
    //  - strip the leading "<METRIC> <family>:" prefix when present, since
    //    the same text already lives in the subtitle right above
    const rawExplanation = alert.reasonText || displayLabelFor(alert) || ''
    const subtitlePrefixRe = new RegExp(
      `^\\s*${METRIC_SHORT[alert.primaryMetric]}\\s+${FAMILY_LABELS[alert.alertFamily]}\\s*:\\s*`,
      'i'
    )
    const explanation = rawExplanation
      .replace(/anchor/gi, 'baseline')
      .replace(/previous 6m/gi, '6m baseline')
      .replace(subtitlePrefixRe, '')
      // Sentence-case the result after stripping the prefix.
      .replace(/^(.)/, (c) => c.toUpperCase())

    // Impact line — only render if the detector ships an estimated-loss
    // label. We don't synthesize one to avoid pretending we have a number.
    const impact = alert.estimatedLossLabel || ''

    // Timing — first-seen + days-active form a "when did it start, how long
    // has it been broken" line.
    const firstSeen = alert.alertFirstSeenDate
      ? safeFormat(alert.alertFirstSeenDate, 'MMM d, yyyy')
      : null
    const daysActive = alert.alertDaysActive ?? 0
    const timing =
      firstSeen && daysActive > 0
        ? `First seen ${firstSeen} · active for ${daysActive} day${daysActive === 1 ? '' : 's'}`
        : firstSeen
        ? `First seen ${firstSeen}`
        : null

    return (
      <div className="card anomalies-status-banner anomalies-status-banner-alert">
        <div className="card-body">
          <div className="row row-space-between row-center anomalies-banner-top">
            <div className="anomalies-badge-row">
              <span
                className="anomalies-family-pill"
                style={{ background: `${familyColor}1A`, color: familyColor }}
              >
                {FAMILY_LABELS[alert.alertFamily]}
              </span>
              {badges.map((b) => (
                <span
                  key={b.label}
                  className={`anomalies-status-badge anomalies-status-badge-${b.variant}`}
                >
                  {b.label}
                </span>
              ))}
            </div>
            <div className="anomalies-banner-priority">
              <span className="text-light text-small">Priority</span>{' '}
              <span className="text-dark text-bolder">
                {formatScore(alert.priorityScore)}
              </span>
            </div>
          </div>
          <div className="text-dark text-bigger text-bolder m-t-2">{subtitle}</div>
          {dateText && (
            <div className="text-light text-small m-t-1">
              Selected date {dateText}
            </div>
          )}
          {explanation && (
            <div className="text-dark m-t-2">{explanation}</div>
          )}
          {impact && (
            <div className="text-dark m-t-2">
              <b>Impact:</b> {impact}
            </div>
          )}
          {timing && (
            <div className="text-light text-small m-t-2">{timing}</div>
          )}
          <NetworkSummary
            networks={networks}
            todayNetworks={todayNetworks}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    )
  }

  // No-alert mode — happens when user selected a date with no alert for the
  // chosen device/metric, or arrived via search with no alert context.
  return (
    <div className="card anomalies-status-banner anomalies-status-banner-info">
      <div className="card-body">
        <div className="text-dark text-bigger text-bolder">
          No active alert for this selection
          {dateText ? ` on ${dateText}` : ''}.
        </div>
        <div className="text-light text-small m-t-1">
          {selectedDevice} · {METRIC_LABELS[selectedMetric]}
          {latestAlertDate && (
            <>
              {' · '}Latest alert{' '}
              {onPickLatestAlert ? (
                <button
                  type="button"
                  className="anomalies-latest-alert-link"
                  onClick={onPickLatestAlert}
                  title="Jump to this date"
                >
                  {safeFormat(latestAlertDate, 'MMM d, yyyy')}
                </button>
              ) : (
                <b>{safeFormat(latestAlertDate, 'MMM d, yyyy')}</b>
              )}
            </>
          )}
          {alertCount > 0 && (
            <>
              {' · '}
              {alertCount} alert{alertCount === 1 ? '' : 's'} in selected period
            </>
          )}
        </div>
        <div className="text-light text-small m-t-1 anomalies-status-banner-hint">
          Showing current health and historical context.
        </div>
        <NetworkSummary
          networks={networks}
          todayNetworks={todayNetworks}
          selectedDate={selectedDate}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Device × metric health matrix — shows whether other (device, metric) combos
// are also affected on the selected date. Click a cell to switch focus.
// ---------------------------------------------------------------------------
// Network chips for the alert summary card.
//
//  - inline "Networks on <date>: X, Y" sentence — what's active right now
//  - chip strip below: the *other* networks the advertiser has used in the
//    window, labelled with "last active N days ago" (or a date for very
//    stale ones). Tells the operator "we used Partner-ads three weeks ago,
//    not anymore."
//
// We don't repeat networks that are already named in the inline sentence,
// and we don't surface the "total active days" count — that number doesn't
// help the investigation.
const NetworkSummary = (props: {
  networks: IAnomaliesV2Network[]
  todayNetworks: IAnomaliesV2SeriesPointNetworkRef[]
  selectedDate?: string
}) => {
  const { networks, todayNetworks, selectedDate } = props
  if (networks.length === 0 && todayNetworks.length === 0) return null

  const todayUuids = new Set(todayNetworks.map((n) => n.uuid))
  const selectedMs = selectedDate ? parseISO(selectedDate).getTime() : null

  const daysSinceLast = (network: IAnomaliesV2Network): number | null => {
    if (selectedMs == null) return null
    try {
      const lastMs = parseISO(network.lastSeen).getTime()
      return Math.max(0, Math.round((selectedMs - lastMs) / 86400000))
    } catch {
      return null
    }
  }

  // Networks the advertiser has used in the window but that aren't active on
  // the selected date. Sorted by most-recent inactivity first so the latest
  // dropoff is the eye's first stop.
  const others = networks
    .filter((n) => !todayUuids.has(n.uuid))
    .map((n) => ({ network: n, daysAgo: daysSinceLast(n) }))
    .sort((a, b) => {
      // null daysAgo (no selectedDate) preserves original order
      if (a.daysAgo == null || b.daysAgo == null) return 0
      return a.daysAgo - b.daysAgo
    })

  const formatLastActive = (daysAgo: number | null, lastSeen: string): string => {
    if (daysAgo == null) return `Last active ${safeFormat(lastSeen, 'MMM d') || lastSeen}`
    if (daysAgo === 0) return 'Last active today'
    if (daysAgo === 1) return 'Last active yesterday'
    if (daysAgo < 60) return `Last active ${daysAgo}d ago`
    return `Last active ${safeFormat(lastSeen, 'MMM d, yyyy') || lastSeen}`
  }

  // Lapsed = hasn't been seen in 30+ days. Faded chip styling so it reads as
  // historical rather than current context.
  const isLapsed = (daysAgo: number | null): boolean =>
    daysAgo != null && daysAgo > 30

  return (
    <div className="m-t-2 anomalies-network-summary">
      {todayNetworks.length > 0 && (
        <div className="text-dark text-small">
          <span className="text-light">
            Networks{selectedDate ? ` on ${safeFormat(selectedDate, 'MMM d')}` : ''}:
          </span>{' '}
          {todayNetworks.map((n) => n.name).join(', ')}
        </div>
      )}
      {others.length > 0 && (
        <div className="anomalies-network-chip-row m-t-1">
          {others.map(({ network, daysAgo }) => (
            <span
              key={network.uuid}
              className={
                'anomalies-network-chip' +
                (isLapsed(daysAgo) ? ' anomalies-network-chip-lapsed' : '')
              }
              title={`First seen ${network.firstSeen} · Last seen ${network.lastSeen}`}
            >
              <span className="anomalies-network-chip-name">{network.name}</span>
              <span className="anomalies-network-chip-days">
                {formatLastActive(daysAgo, network.lastSeen)}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const HealthMatrix = (props: {
  sameDateAlerts: IAnomaliesV2RelatedAlert[]
  alertHistory: IAnomaliesV2RelatedAlert[]
  selectedAlert: IAnomaliesV2Alert | null
  selectedDate: string | null
  selectedDevice: TAnomaliesDevice | null
  selectedMetric: TAnomaliesPrimaryMetric
  onPick: (device: TAnomaliesDevice, metric: TAnomaliesPrimaryMetric) => void
}) => {
  const {
    sameDateAlerts,
    alertHistory,
    selectedAlert,
    selectedDate,
    selectedDevice,
    selectedMetric,
    onPick,
  } = props

  // sameDateAlerts often excludes the currently-investigated alert (it's
  // "OTHER alerts on this date"), so fold in selectedAlert plus any
  // alertHistory rows on the selected date as fallback sources.
  const allOnDate: IAnomaliesV2RelatedAlert[] = useMemo(() => {
    if (!selectedDate) return []
    const out: IAnomaliesV2RelatedAlert[] = []
    const seen = new Set<string>()
    const push = (a: IAnomaliesV2RelatedAlert | null | undefined) => {
      if (!a) return
      if (a.eventDate?.slice(0, 10) !== selectedDate) return
      const key = `${a.device}_${a.primaryMetric}`
      if (seen.has(key)) return
      seen.add(key)
      out.push(a)
    }
    if (selectedAlert) {
      push({
        eventDate: selectedAlert.eventDate,
        device: selectedAlert.device,
        alertFamily: selectedAlert.alertFamily,
        primaryMetric: selectedAlert.primaryMetric,
        priorityScore: selectedAlert.priorityScore,
        reasonText: selectedAlert.reasonText,
        displayReason: selectedAlert.displayReason,
      })
    }
    sameDateAlerts.forEach(push)
    alertHistory.forEach(push)
    return out
  }, [selectedDate, selectedAlert, sameDateAlerts, alertHistory])

  const cellAlert = (
    d: TAnomaliesDevice,
    m: TAnomaliesPrimaryMetric
  ): IAnomaliesV2RelatedAlert | null => {
    return allOnDate.find((a) => a.device === d && a.primaryMetric === m) || null
  }

  return (
    <div className="card anomalies-health-matrix">
      <div className="card-body">
        <div className="text-dark text-bolder m-b-2">
          Health on selected date
        </div>
        <div className="text-light text-small m-b-2">
          Click any cell to focus the page on that device and metric.
        </div>
        <div className="table-container-overflow">
          <table className="table table-bordered anomalies-matrix-table">
            <thead>
              <tr>
                <th></th>
                {METRICS.map((m) => (
                  <th key={m} className="text-center">
                    {METRIC_SHORT[m]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DEVICES.map((d) => (
                <tr key={d}>
                  <td className="text-bold">{d}</td>
                  {METRICS.map((m) => {
                    const alert = cellAlert(d, m)
                    const isSelected =
                      selectedDevice === d && selectedMetric === m
                    return (
                      <td
                        key={m}
                        className={varClass({
                          'anomalies-matrix-cell': true,
                          'anomalies-matrix-cell-active': !!alert,
                          'anomalies-matrix-cell-selected': isSelected,
                          [`anomalies-matrix-cell-${alert?.alertFamily || ''}`]:
                            !!alert,
                        })}
                      >
                        <button
                          type="button"
                          className="anomalies-matrix-btn"
                          onClick={() => onPick(d, m)}
                        >
                          {alert ? (
                            <>
                              <div className="anomalies-matrix-family">
                                {FAMILY_LABELS[alert.alertFamily]}
                              </div>
                              <div className="anomalies-matrix-score">
                                {formatScore(alert.priorityScore)}
                              </div>
                            </>
                          ) : (
                            <span className="text-light">OK</span>
                          )}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metric-specific story — single card, prose-style. Renders one or two
// sentences that read naturally instead of a grid of value tiles.
// ---------------------------------------------------------------------------
const MetricEvidenceCards = (props: {
  metric: TAnomaliesPrimaryMetric
  point: IAnomaliesV2SeriesPoint | undefined
  alert: IAnomaliesV2Alert | null
  device: TAnomaliesDevice | null
  selectedDate: string | null
}) => {
  const { metric, point, alert, device, selectedDate } = props
  if (!point) return null

  // Pick the "current window" length the story actually talks about, so the
  // sub-header date range matches what each sentence references. Drift uses
  // the 30d window; acute volume metrics and the rejection volume series both
  // use the 7d window.
  const isDriftRate = metric === 'cr' || metric === 'epc' || metric === 'spc'
  const currentDays = isDriftRate ? 30 : 7
  const windowLabel = isDriftRate ? 'last 30 days' : 'last 7 days'

  const lines = storyFor(metric, point as any, alert, selectedDate, currentDays)
  if (lines.length === 0) return null

  const range = formatWindowRange(selectedDate, currentDays)
  const meta =
    device || range
      ? `${device ? device + ' · ' : ''}${windowLabel}${range ? ` (${range})` : ''}`
      : null

  return (
    <div className="card anomalies-key-facts m-t-4">
      <div className="card-body">
        <div className="text-light text-small m-b-1">Summary</div>
        {meta && (
          <div className="text-light text-small m-b-2">{meta}</div>
        )}
        {lines.map((line, i) => (
          <div
            key={i}
            className={`text-dark anomalies-key-facts-line${
              i > 0 ? ' m-t-2' : ''
            }`}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  )
}

// Formats the N-day window ending on `endDate` as "MMM d – MMM d, yyyy". The
// year is omitted on the start date when the window doesn't cross a year
// boundary, since "Apr 13 – May 12, 2026" is easier to scan than
// "Apr 13, 2026 – May 12, 2026".
const formatWindowRange = (
  endDate: string | null,
  days: number
): string | null => {
  if (!endDate) return null
  try {
    const end = parseISO(endDate)
    const start = new Date(end.getTime() - (days - 1) * 86400000)
    return formatDateSpan(start, end)
  } catch {
    return null
  }
}

// Formats the `length`-day window that ends `offset` days before `endDate`.
// Used for "6m baseline" etc., where the baseline excludes the current
// window. Example: end=May 12, offset=30, length=180 ⇒ Oct 15, 2025 – Apr 12,
// 2026.
const formatPreviousWindowRange = (
  endDate: string | null,
  offset: number,
  length: number
): string | null => {
  if (!endDate) return null
  try {
    const anchor = parseISO(endDate)
    const end = new Date(anchor.getTime() - offset * 86400000)
    const start = new Date(end.getTime() - (length - 1) * 86400000)
    return formatDateSpan(start, end)
  } catch {
    return null
  }
}

const formatDateSpan = (start: Date, end: Date): string => {
  const endLabel = format(end, 'MMM d, yyyy')
  const startLabel =
    start.getFullYear() === end.getFullYear()
      ? format(start, 'MMM d')
      : format(start, 'MMM d, yyyy')
  return `${startLabel} – ${endLabel}`
}

// Joins a list of inline nodes with commas and "and" before the last item —
// e.g. [A, B, C] -> "A, B and C".
const joinWithAnd = (parts: React.ReactNode[]): React.ReactNode => {
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0]
  if (parts.length === 2)
    return (
      <>
        {parts[0]} and {parts[1]}
      </>
    )
  return (
    <>
      {parts.slice(0, -1).map((p, i) => (
        <React.Fragment key={i}>
          {p}
          {i < parts.length - 2 ? ', ' : ' and '}
        </React.Fragment>
      ))}
      {parts[parts.length - 1]}
    </>
  )
}

const storyFor = (
  metric: TAnomaliesPrimaryMetric,
  p: any,
  alert: IAnomaliesV2Alert | null,
  selectedDate: string | null,
  _currentDays: number
): React.ReactNode[] => {
  const short = METRIC_SHORT[metric]
  const lines: React.ReactNode[] = []

  if (metric === 'cr' || metric === 'epc' || metric === 'spc') {
    const cur = formatMetricSmart(p[`${metric}30dCurr`], metric)
    const baseline = formatMetricSmart(p[`${metric}180dAnchor`], metric)
    const delta = formatVsAnchor(p[`${metric}DriftRatio`]) || 'in line with baseline'

    // Inline baseline date range so the operator knows the 6m anchor window
    // didn't include the current 30d. The current-30d window is already
    // disclosed in the card's sub-header.
    const baselineRange = formatPreviousWindowRange(selectedDate, 30, 180)

    lines.push(
      <>
        Current 30d {short} of <b>{cur}</b> is <b>{delta}</b>, vs the 6m
        baseline of <b>{baseline}</b>
        {baselineRange ? <> ({baselineRange})</> : null}.
      </>
    )

    // Estimated impact phrased per metric. Skip parts that are zero/missing
    // so the sentence doesn't pretend a number we don't have.
    const impactParts: React.ReactNode[] = []
    if (metric === 'cr') {
      if (isMeaningful(p.estLostConversions30d)) {
        impactParts.push(
          <>
            <b>{formatInteger(p.estLostConversions30d)}</b> lost conversions
          </>
        )
      }
      if (isMeaningful(p.estLostSaleFromCrDkk)) {
        impactParts.push(
          <>
            <b>{formatDkkLarge(p.estLostSaleFromCrDkk)}</b> in lost sale
          </>
        )
      }
    }
    if (metric === 'epc' && isMeaningful(p.estLostCommission30dDkk)) {
      impactParts.push(
        <>
          <b>{formatDkkLarge(p.estLostCommission30dDkk)}</b> in lost commission
        </>
      )
    }
    if (metric === 'spc' && isMeaningful(p.estLostSale30dDkk)) {
      impactParts.push(
        <>
          <b>{formatDkkLarge(p.estLostSale30dDkk)}</b> in lost sale
        </>
      )
    }
    if (impactParts.length > 0) {
      lines.push(<>Estimated 30d impact: {joinWithAnd(impactParts)}.</>)
    }

    // Commission-missing surfaces the suspicious "sales but no commission"
    // pattern in a single sentence on EPC drift.
    if (metric === 'epc' && alert && isCommissionMissing(alert)) {
      lines.push(
        <>
          Tracking signal: <b>0 DKK</b> commission recorded while{' '}
          <b>{formatInteger(p.conversions30dCurr)}</b> conversions and{' '}
          <b>{formatDkkLarge(p.sale30dCurrDkk)}</b> in sale were registered
          over the same 30 days.
        </>
      )
    }
    return lines
  }

  if (metric === 'clicks' || metric === 'conversions') {
    const cur = metric === 'clicks' ? p.clicks7d : p.conversions7d
    const base = p[`${metric}30dCurr`]
    const ratio = cur != null && base ? cur / base : null
    const delta = formatVsAnchor(ratio) || 'in line with baseline'
    const baselineRange = formatWindowRange(selectedDate, 30)
    lines.push(
      <>
        7d {short} of <b>{formatInteger(cur)}</b> is <b>{delta}</b>, vs the
        30d baseline of <b>{formatInteger(base)}</b>
        {baselineRange ? <> ({baselineRange})</> : null}.
      </>
    )
    return lines
  }

  return lines
}


const formatMetricSmart = (
  v: number | null | undefined,
  m: TAnomaliesPrimaryMetric
): string => {
  if (v == null || isNaN(v)) return '—'
  if (m === 'cr') return formatPercent(v)
  if (m === 'epc' || m === 'spc') return formatDkkPrecise(v)
  return formatInteger(v)
}

// Numeric guard: useful for filtering out zero-valued impact cards before
// they render. Keeps the key-facts grid from filling up with "0 DKK" tiles
// that don't actually inform.
const isMeaningful = (v: number | null | undefined): boolean =>
  v != null && !isNaN(v) && v > 0


// ---------------------------------------------------------------------------
// Related alerts — grouped into periods.
// ---------------------------------------------------------------------------
const RelatedAlertsPanel = (props: {
  advertiserDomain: string
  sameDate: IAnomaliesV2RelatedAlert[]
  nearby: IAnomaliesV2RelatedAlert[]
  onPick: (row: IAnomaliesV2RelatedAlert) => void
}) => {
  const { sameDate, nearby, onPick } = props
  const sameDatePeriods = useMemo(() => groupAlertPeriods(sameDate), [sameDate])
  const nearbyPeriods = useMemo(() => groupAlertPeriods(nearby), [nearby])
  const totalPeriods = sameDatePeriods.length + nearbyPeriods.length

  return (
    <div className="card anomalies-related-card">
      <div className="card-body">
        <div className="row row-space-between row-center m-b-2">
          <div className="text-dark text-bolder">
            Related alerts{' '}
            <span className="text-light text-small">({totalPeriods})</span>
          </div>
        </div>
        {totalPeriods === 0 ? (
          <div className="text-light text-small p-y-3">
            No other alerts on the selected date or in the surrounding window.
          </div>
        ) : (
          <>
            {sameDatePeriods.length > 0 && (
              <>
                <div className="text-light text-small m-b-1">
                  Same date ({sameDatePeriods.length})
                </div>
                <div className="anomalies-period-list m-b-3">
                  {sameDatePeriods.map((p, i) => (
                    <PeriodRow
                      key={`sd_${p.device}_${p.alertFamily}_${p.primaryMetric}_${p.startDate}_${i}`}
                      period={p}
                      onPick={onPick}
                    />
                  ))}
                </div>
              </>
            )}
            {nearbyPeriods.length > 0 && (
              <>
                <div className="text-light text-small m-b-1">
                  Nearby ±14 days ({nearbyPeriods.length})
                </div>
                <div className="anomalies-period-list">
                  {nearbyPeriods.map((p, i) => (
                    <PeriodRow
                      key={`nb_${p.device}_${p.alertFamily}_${p.primaryMetric}_${p.startDate}_${i}`}
                      period={p}
                      onPick={onPick}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const PeriodRow = (props: {
  period: AlertPeriod
  onPick: (row: IAnomaliesV2RelatedAlert) => void
}) => {
  const { period, onPick } = props
  const [open, setOpen] = useState(false)
  const familyColor = FAMILY_COLORS[period.alertFamily]

  const dateLabel =
    period.startDate === period.endDate
      ? safeFormat(period.startDate, 'MMM d')
      : `${safeFormat(period.startDate, 'MMM d')} – ${safeFormat(period.endDate, 'MMM d')}`

  // Apply this period's (device, metric, family) to the page state and pin
  // the selected date to the period's latest day — that's the most-actionable
  // entry point.
  const handleApply = (e: React.MouseEvent) => {
    e.stopPropagation()
    const last = period.rows[period.rows.length - 1]
    onPick(last)
  }

  // Reads as e.g. "Mobile · CR drift · Apr 21 – Apr 27 · 7 alert days · max score 26.2"
  const summary =
    `${period.device} · ${METRIC_SHORT[period.primaryMetric]} ${FAMILY_LABELS[period.alertFamily]}` +
    ` · ${dateLabel} · ${period.alertDays} alert day${period.alertDays === 1 ? '' : 's'}` +
    ` · max score ${formatScore(period.maxPriorityScore)}`

  return (
    <div className="anomalies-period">
      <div className="anomalies-period-row">
        <button
          type="button"
          className="anomalies-period-toggle-btn"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? '▾' : '▸'}
        </button>
        <button
          type="button"
          className="anomalies-period-summary"
          onClick={handleApply}
          title="Apply this period (sets device, metric, and selected date)"
        >
          <span
            className="anomalies-family-pill"
            style={{ background: `${familyColor}1A`, color: familyColor }}
          >
            {FAMILY_LABELS[period.alertFamily]}
          </span>
          <span className="text-dark text-small">{summary}</span>
        </button>
      </div>
      {open && (
        <div className="anomalies-period-rows">
          {period.rows
            .slice()
            .reverse()
            .map((row) => (
              <button
                key={`${row.eventDate}_${row.device}_${row.primaryMetric}`}
                type="button"
                className="anomalies-period-day"
                onClick={() => onPick(row)}
              >
                <span className="text-light text-small">
                  {safeFormat(row.eventDate, 'MMM d, yyyy')}
                </span>
                <span className="text-light text-small">
                  {row.reasonText || row.displayReason || ''}
                </span>
                <span className="text-bold text-small">
                  {formatScore(row.priorityScore)}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Advertiser alert history — same period grouping over the full list.
// ---------------------------------------------------------------------------
const AlertHistorySection = (props: {
  advertiserDomain: string
  alerts: IAnomaliesV2RelatedAlert[]
  onPick: (row: IAnomaliesV2RelatedAlert) => void
}) => {
  const { alerts, onPick } = props
  const [open, setOpen] = useState(false)
  const periods = useMemo(() => groupAlertPeriods(alerts), [alerts])

  return (
    <div className="card anomalies-related-card">
      <div className="card-body">
        <div className="row row-space-between row-center m-b-2">
          <div className="text-dark text-bolder">
            Alert history{' '}
            <span className="text-light text-small">
              ({periods.length} period{periods.length === 1 ? '' : 's'} ·{' '}
              {alerts.length} day{alerts.length === 1 ? '' : 's'})
            </span>
          </div>
          {periods.length > 0 && (
            <button
              type="button"
              className="anomalies-history-toggle-inline text-light text-small"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Hide' : 'Show'}
            </button>
          )}
        </div>
        {periods.length === 0 ? (
          <div className="text-light text-small p-y-3">
            No alert history in the selected range.
          </div>
        ) : open ? (
          <div className="anomalies-period-list">
            {periods.map((p, i) => (
              <PeriodRow
                key={`${p.device}_${p.alertFamily}_${p.primaryMetric}_${p.startDate}_${i}`}
                period={p}
                onPick={onPick}
              />
            ))}
          </div>
        ) : (
          <div className="text-light text-small p-y-3">
            Click{' '}
            <button
              type="button"
              className="anomalies-inline-link"
              onClick={() => setOpen(true)}
            >
              Show
            </button>{' '}
            to expand all {periods.length} period
            {periods.length === 1 ? '' : 's'}.
          </div>
        )}
      </div>
    </div>
  )
}

// Compact all-metrics table for the advanced expander. Shows everything the
// detector reasoned over, side-by-side, so the user can sanity-check why
// drift triggered for one metric and not another.
const AdvancedDetectorTable = (props: {
  point: IAnomaliesV2SeriesPoint | undefined
  series: IAnomaliesV2SeriesPoint[]
  selectedDate?: string
}) => {
  const { point, series, selectedDate } = props
  if (!point) {
    return (
      <div className="text-light text-small p-y-6 text-center">
        No detector row for the selected date.
      </div>
    )
  }
  const p = point as any

  // Sum a daily field across the N calendar days ending on the selected date.
  // Used for sale/commission (which the detector never pre-aggregates) and as
  // a fallback for clicks/conversions when the detector doesn't ship totals.
  const sumLastN = (field: keyof IAnomaliesV2SeriesPoint, n: number): number | null => {
    if (!selectedDate) return null
    const endMs = parseISO(selectedDate).getTime()
    const startMs = endMs - (n - 1) * 86400000
    let total = 0
    let any = false
    for (const row of series) {
      const t = parseISO(row.eventDate).getTime()
      if (t < startMs || t > endMs) continue
      const v = (row as any)[field]
      if (v != null && !isNaN(v)) {
        total += v
        any = true
      }
    }
    return any ? total : null
  }

  // Synthetic rows aren't real TAnomaliesPrimaryMetric values, so this builds
  // a row spec per UI row instead of switching on the metric.
  interface DetectorRow {
    label: string
    curr: number | null | undefined
    anchor: number | null | undefined
    prior90: number | null | undefined
    driftRatio: number | null | undefined
    recentRatio: number | null | undefined
    z7v30: number | null | undefined
    z7v90: number | null | undefined
    fmtKind: 'rate' | 'cash' | 'int'
  }

  const rateRow = (metric: 'cr' | 'epc' | 'spc', label: string): DetectorRow => ({
    label,
    curr: p[`${metric}30dCurr`],
    anchor: p[`${metric}180dAnchor`],
    prior90: p[`${metric}90dBase`] ?? p[`${metric}Prior90d`],
    driftRatio: p[`${metric}DriftRatio`],
    recentRatio: p[`${metric}RecentRatio`],
    z7v30: p[`${metric}7v30Z`],
    z7v90: p[`${metric}7v90Z`],
    fmtKind: metric === 'cr' ? 'rate' : 'cash',
  })

  const countRow = (
    field: 'clicks' | 'conversions',
    label: string
  ): DetectorRow => {
    const curr = (p as any)[`${field}30dCurr`] ?? sumLastN(field, 30)
    const anchor = (p as any)[`${field}180dAnchor`] ?? sumLastN(field, 180)
    const prior90 = (p as any)[`${field}90dBase`] ?? sumLastN(field, 90)
    return {
      label,
      curr,
      anchor,
      prior90,
      driftRatio:
        curr != null && anchor != null && anchor > 0 ? curr / anchor : null,
      recentRatio:
        curr != null && prior90 != null && prior90 > 0 ? curr / prior90 : null,
      z7v30: p[`${field}7v30Z`],
      z7v90: p[`${field}7v90Z`],
      fmtKind: 'int',
    }
  }

  // Sale and commission: the detector never ships these as windowed totals,
  // so we sum daily DKK values directly from the series.
  const cashRow = (
    field: 'saleDkk' | 'commissionDkk',
    label: string
  ): DetectorRow => {
    const curr = sumLastN(field, 30)
    const anchor = sumLastN(field, 180)
    const prior90 = sumLastN(field, 90)
    return {
      label,
      curr,
      anchor,
      prior90,
      driftRatio:
        curr != null && anchor != null && anchor > 0 ? curr / anchor : null,
      recentRatio:
        curr != null && prior90 != null && prior90 > 0 ? curr / prior90 : null,
      z7v30: null,
      z7v90: null,
      fmtKind: 'cash',
    }
  }

  // The detector's active rejection signal — volume of rejections reported in
  // the last 7d vs the weekly-equivalent 30d/90d baselines.
  const rejectionVolumeRow = (): DetectorRow => {
    const cur = p.reportedRej7d ?? null
    const anchor =
      p.reportedRej30dBase != null ? (p.reportedRej30dBase / 30) * 7 : null
    const prior90 =
      p.reportedRej90dBase != null ? (p.reportedRej90dBase / 30) * 7 : null
    return {
      label: 'Reported rejections (7d)',
      curr: cur,
      anchor,
      prior90,
      driftRatio:
        cur != null && anchor != null && anchor > 0 ? cur / anchor : null,
      recentRatio:
        cur != null && prior90 != null && prior90 > 0 ? cur / prior90 : null,
      z7v30: p.reportedRej7v30Z,
      z7v90: p.reportedRej7v90Z,
      fmtKind: 'int',
    }
  }

  const rows: DetectorRow[] = [
    rateRow('cr', 'CR'),
    rateRow('epc', 'EPC'),
    rateRow('spc', 'SPC'),
    countRow('clicks', 'Clicks'),
    countRow('conversions', 'Conversions'),
    rejectionVolumeRow(),
    cashRow('saleDkk', 'Sale (DKK)'),
    cashRow('commissionDkk', 'Commission (DKK)'),
  ]

  const fmtValue = (v: number | null | undefined, kind: DetectorRow['fmtKind']) => {
    if (v == null || isNaN(v)) return '—'
    if (kind === 'rate') return formatPercent(v)
    if (kind === 'cash') return formatDkkLarge(v)
    return formatInteger(v)
  }
  const ratioFmt = (v: number | null | undefined) =>
    v == null || isNaN(v) ? '—' : formatDecimal(v, 2)

  return (
    <div className="table-container-overflow">
      <table className="table table-bordered anomalies-advanced-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th className="text-right">Current 30d</th>
            <th className="text-right">Rolling 6m anchor</th>
            <th className="text-right">Prior 90d</th>
            <th className="text-right">Anchor ratio</th>
            <th className="text-right">Recent ratio</th>
            <th className="text-right">7v30 z</th>
            <th className="text-right">7v90 z</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td className="text-dark text-bold">{r.label}</td>
              <td className="text-right">{fmtValue(r.curr, r.fmtKind)}</td>
              <td className="text-right">{fmtValue(r.anchor, r.fmtKind)}</td>
              <td className="text-right">{fmtValue(r.prior90, r.fmtKind)}</td>
              <td className="text-right">{ratioFmt(r.driftRatio)}</td>
              <td className="text-right">{ratioFmt(r.recentRatio)}</td>
              <td className="text-right">{ratioFmt(r.z7v30)}</td>
              <td className="text-right">{ratioFmt(r.z7v90)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

